import { Box, Chip, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { useAuth } from "../../hooks/AuthContext";
import Grid from "@mui/material/Grid2";
import { listCustomerOrders } from "../../utilities/service";
import useApiQuery from "../../hooks/useApiQuery";
import { ProfileSkeleton } from "./ProfileSkeleton";
import NoOrdersFound from "./NoOrdersFound";
import OrderItem from "./OrderItem";

function MyOrders() {
  const { user } = useAuth();

  const {
    data: orderList,
    error,
    isLoading,
    refetch,
  } = useApiQuery({
    queryKey: ["listCustomerOrders"],
    queryFn: () => listCustomerOrders(user),
  });

  if (isLoading) return <ProfileSkeleton />;
  if (!isLoading && orderList?.length === 0) return <NoOrdersFound />;

  const deliveredCount = orderList?.filter(
    (item) => item?.preview_status === "Delivered",
  ).length;

  return (
    <Box>
      <OrdersHeroCard>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "24px", md: "30px" },
            color: "#2B241A",
            lineHeight: "130%",
            mb: 0.75,
          }}
        >
          My Orders
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "#6E624B",
            maxWidth: 560,
            mb: 2,
          }}
        >
          Track every dispatch, delivery milestone, and completed order from one place.
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <OrdersBadge
            icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}
            label={`${orderList?.length || 0} total orders`}
          />
          <OrdersBadge
            icon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}
            label={`${Math.max((orderList?.length || 0) - deliveredCount, 0)} active updates`}
          />
          <OrdersBadge
            icon={<VerifiedOutlinedIcon sx={{ fontSize: 16 }} />}
            label={`${deliveredCount} delivered`}
          />
        </Stack>
      </OrdersHeroCard>

      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: "150%",
          color: "var(--neutral-black)",
          textAlign: "left",
          position: "relative",
          pb: 1,
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "44px",
            height: "3px",
            borderRadius: "2px",
            background: "var(--primary-red-base)",
          },
        }}
      >
        Order Timeline
      </Typography>
      <Grid container spacing={2.5} sx={{ mt: 4 }}>
        {orderList?.map((item) => (
          <OrderItem key={item?.order_id} item={item} />
        ))}
      </Grid>
    </Box>
  );
}

const OrdersHeroCard = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.24)",
  borderRadius: "18px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  background:
    "radial-gradient(circle at top left, rgba(255,247,231,0.95) 0%, rgba(255,255,255,0.98) 48%, rgba(255,250,242,0.95) 100%)",
  boxShadow: "0 14px 30px rgba(173, 132, 61, 0.1)",
}));

const OrdersBadge = styled(Chip)(() => ({
  borderRadius: "999px",
  background: "rgba(255, 246, 225, 0.95)",
  border: "1px solid rgba(188, 128, 15, 0.24)",
  color: "#6B5425",
  fontWeight: 600,
}));

export default MyOrders;
